import { Field, FieldArray, Form, Formik, FieldProps } from "formik";
import MenuPlacement, { menuInitial, menuPayload } from "../../../shared/MenuPlacement";
import React, { useEffect, useState } from "react";
import Paragraph from "../Paragraphs/Paragraph";
import { get } from "../../../../_api";
import ReactSelect from "react-select";
import {
  Box,
  Button,
  Checkbox,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";

export const reactSelectStyle = {
  option: (styles: Record<string, unknown>) => {
    return {
      ...styles,
      fontSize: "14px",
    };
  },
  control: (base: Record<string, unknown>, state: unknown) => {
    return {
      ...base,
      "&:hover": {
        borderColor: "orange",
      },
      border: "2px solid orange",
      borderRadius: "5px",
      fontSize: "14px",
      outline: "none",
      width: "100%",
      maxWidth: "100%",
      boxShadow: "none",
    };
  },
};

const mapOffersToSelectValues = (offer: IOffer[]): SelectValues[] => {
  return offer.map((el) => ({ label: el.title, value: el._id }));
};

export interface IOffer {
  _id: string;
  title: string;
  shortDescription: string;
  slug: string;
  main: boolean;
  subOffers: IOffer[];
  meta: {
    title: string;
    keyWords: string;
    description: string;
  };
  paragraphs: [
    {
      id: number;
      title: string;
      text: string;
      quote: string;
      fade: boolean;
      order: string;
    }
  ];
}

type Props = {
  onSubmit(values: unknown): void;
  offer?: IOffer;
};

export type SelectValues = {
  label: string;
  value: string;
};

const OfferForm: React.FC<Props> = ({ onSubmit, offer }) => {
  const [subOffers, setSubOffers] = useState<SelectValues[]>([]);
  useEffect(() => {
    get("offers/ids").then((data) => {
      setSubOffers(mapOffersToSelectValues(data));
    });
  }, []);

  return (
    <Formik
      onSubmit={(values) => {
        onSubmit({ ...values, ...menuPayload(values) });
      }}
      enableReinitialize={Boolean(offer)}
      initialValues={{
        title: offer?.title || "",
        shortDescription: offer?.shortDescription || "",
        slug: offer?.slug || "",
        main: offer?.main || false,
        ...menuInitial(offer),
        meta: {
          title: offer?.meta.title || "",
          keyWords: offer?.meta.keyWords || "",
          description: offer?.meta.description || "",
        },
        subOffers: offer?.subOffers
          ? mapOffersToSelectValues(offer.subOffers)
          : [],
        paragraphs: offer?.paragraphs || [
          {
            id: Math.random(),
            h: "h2",
            title: "",
            text: "",
            quote: "",
            fade: false,
            defaultFaded: false,
            order: "",
          },
        ],
      }}
    >
      {(props) => (
        <Form>
          <Stack gap="md">
            <Field name="title">
              {({ field, meta }: FieldProps) => (
                <TextInput label="Tytuł" {...field} error={meta.touched ? meta.error : undefined} />
              )}
            </Field>
            <Field name="shortDescription">
              {({ field, meta }: FieldProps) => (
                <Textarea
                  label="Krótki opis (pod nagłówkiem)"
                  rows={5}
                  resize="vertical"
                  {...field}
                  error={meta.touched ? meta.error : undefined}
                />
              )}
            </Field>
            <Field name="slug">
              {({ field, meta }: FieldProps) => (
                <TextInput label="Slug" {...field} error={meta.touched ? meta.error : undefined} />
              )}
            </Field>
            <Field name="main">
              {({ field, form }: FieldProps) => (
                <Checkbox
                  label="Główna oferta"
                  description="Kategoria najwyższego poziomu na stronie /oferta. To nie to samo co „Główna” niżej, która dotyczy strony startowej."
                  checked={field.value}
                  onChange={(e) =>
                    form.setFieldValue(field.name, e.currentTarget.checked)
                  }
                />
              )}
            </Field>

            <Text size="sm" fw={500}>
              Pod oferty
            </Text>
            <Text size="xs" c="dimmed">
              Oferty powiązane tematycznie, pokazywane na dole strony tej oferty.
              Nie budują menu — miejsce w menu ustawia się niżej.
            </Text>
            <Box>
              <ReactSelect
                isMulti
                styles={reactSelectStyle}
                value={props.values.subOffers}
                onChange={(newValue) => {
                  if (newValue) {
                    props.setFieldValue("subOffers", newValue);
                  }
                }}
                options={subOffers}
              />
            </Box>

            <MenuPlacement kind="offer" selfId={offer?._id} />

            <Title order={3}>Meta dane</Title>

            <Field name="meta.title">
              {({ field, meta }: FieldProps) => (
                <TextInput label="Tytuł (meta)" {...field} error={meta.touched ? meta.error : undefined} />
              )}
            </Field>
            <Field name="meta.keyWords">
              {({ field, meta }: FieldProps) => (
                <Textarea
                  label="Słowa kluczowe"
                  {...field}
                  error={meta.touched ? meta.error : undefined}
                />
              )}
            </Field>
            <Field name="meta.description">
              {({ field, meta }: FieldProps) => (
                <TextInput
                  label="Opis (description)"
                  {...field}
                  error={meta.touched ? meta.error : undefined}
                />
              )}
            </Field>

            <Title order={3}>Paragrafy</Title>

            <FieldArray name="paragraphs">
              {({ push }) => {
                return (
                  <>
                    {props.values.paragraphs.map((p, i) => (
                      <Paragraph
                        key={`offer-par-${i}-${String(p.id)}`}
                        index={i}
                        onRemove={() => {
                          const value = props.values.paragraphs.filter(
                            (v) => v.id !== p.id
                          );
                          props.setFieldValue("paragraphs", value);
                        }}
                      />
                    ))}
                    <Button
                      type="button"
                      variant="light"
                      color="orange"
                      size="sm"
                      radius="md"
                      onClick={() => {
                        push({
                          id: Math.random(),
                          h: "h2",
                          title: "",
                          text: "",
                          quote: "",
                          fade: false,
                          defaultFaded: false,
                          order: "",
                        });
                      }}
                    >
                      Dodaj paragraf
                    </Button>
                  </>
                );
              }}
            </FieldArray>

            <Button type="submit" size="md" radius="md" fullWidth mt="md">
              {offer ? "Aktualizuj ofertę" : "Dodaj ofertę"}
            </Button>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default OfferForm;
